import React from 'react';
import { AWPotentialReviewerDto } from '@api/Api';
import { useOrganization } from '@components/OrganizationBoundary';
import type { AwStateStageMember } from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import ReviewersSelect from '@components/Modals/ApprovalModal/components/ReviewersSelect/ReviewersSelect';
import { getMemberInitialState } from '@components/Modals/ApprovalModal/utils';
import { nonNull } from '@helpers/non-null';

import './InviteReviewers.less';

type InviteReviewersProps = {
  members: AwStateStageMember[];
  campaignId: string;
  allDecisionsSubmittedChecked: boolean;
  hasDominatingDecisions: boolean;
  stageFinished: boolean;
  onUpdateMembers: (members: AwStateStageMember[]) => void;
};

export default function InviteReviewers({
  members,
  campaignId,
  allDecisionsSubmittedChecked,
  hasDominatingDecisions,
  stageFinished,
  onUpdateMembers
}: InviteReviewersProps) {
  const { currentOrganization } = useOrganization(false);

  const upgradeExternalContributors =
    !currentOrganization?.entity?.features.externalContributors;
  const reviewersLimit =
    currentOrganization?.entity?.limits.approvalWorkflowStageMembers ??
    Number.POSITIVE_INFINITY;
  const reviewersLimitReached = members.length >= reviewersLimit;

  const onAddReviewers = (
    values: { email: string; reviewer?: AWPotentialReviewerDto }[]
  ) => {
    const maxMembersCount = Math.max(reviewersLimit - members.length, 0);
    const newReviewers = values
      .map(({ email, reviewer }) => {
        if (members.some((x) => x.email === email)) {
          return;
        }
        if (!reviewer && upgradeExternalContributors) {
          return;
        }
        return getMemberInitialState({
          email,
          canComment: true,
          canSubmitDecision: true,
          hasDominatingDecision:
            allDecisionsSubmittedChecked && hasDominatingDecisions,
          ...(reviewer ?? {})
        });
      })
      .filter((x) => !!x)
      .map((x) => nonNull(x))
      .slice(0, maxMembersCount)
      .map((x) => ({
        ...x,
        name: x.name || ''
      }));
    onUpdateMembers([...members, ...newReviewers]);
  };

  const onRemoveReviewer = (member: AWPotentialReviewerDto) => {
    onUpdateMembers(members.filter((item) => item.email !== member.email));
  };

  return (
    <div className="invite-reviewers">
      <ReviewersSelect
        campaignId={campaignId}
        excludeEmails={members.map((x) => x.email)}
        limitReached={reviewersLimitReached}
        stageFinished={stageFinished}
        onAddReviewers={onAddReviewers}
        onRemoveReviewer={onRemoveReviewer}
      />
    </div>
  );
}
